import { Helmet } from "react-helmet-async";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Container,
  Link,
  Grid,
  List,
  ListItem,
} from "@mui/material";

import Logo from "../components/Logo";
import SimpleFooter from "../components/SimpleFooter";

const StyledPageContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
});

const ContentWrapper = styled("div")(({ theme }) => ({
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(8),
}));

const sections = [
  {
    id: "interpretation",
    title: "1. Interpretation and Definitions",
    content: (
      <>
        <Typography
          variant="h6"
          component="h3"
          sx={{ fontWeight: 600, mt: 2, mb: 1 }}
        >
          Interpretation
        </Typography>
        <Typography variant="body1" paragraph>
          Words with the first letter capitalized have specific meanings as
          defined below. Singular and plural forms have the same meaning.
        </Typography>
        <Typography
          variant="h6"
          component="h3"
          sx={{ fontWeight: 600, mt: 3, mb: 1 }}
        >
          Definitions
        </Typography>
        <Typography variant="body1" paragraph>
          For the purposes of these Terms of Service:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <Typography variant="body1" paragraph>
            <strong>Account</strong> – a unique account created by You to access
            Contactie
            <Box
              component="sup"
              sx={{ fontSize: "0.6em", verticalAlign: "super" }}
            >
              TM
            </Box>{" "}
            or parts of its Service.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Company</strong> – refers to Contactie
            <Box
              component="sup"
              sx={{ fontSize: "0.6em", verticalAlign: "super" }}
            >
              TM
            </Box>
            , also referred to as "We", "Us", or "Our".
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Content</strong> – any text, images, or other information
            posted, uploaded, linked to, or made available by You.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Service</strong> – the Contactie
            <Box
              component="sup"
              sx={{ fontSize: "0.6em", verticalAlign: "super" }}
            >
              TM
            </Box>{" "}
            website and platform.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Terms</strong> – these Terms of Service.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>You</strong> – the individual accessing or using the
            Service, or the legal entity on behalf of which such individual is
            accessing the Service.
          </Typography>
        </Box>
      </>
    ),
  },
  {
    id: "acknowledgment",
    title: "2. Acknowledgment",
    content: (
      <>
        <Typography variant="body1" paragraph>
          By accessing or using the Service, You agree to these Terms. If You do
          not agree, do not use the Service.
        </Typography>
        <Typography variant="body1" paragraph>
          You represent that you are 18 years or older.
        </Typography>
        <Typography variant="body1" paragraph>
          Your access and use are also governed by our Privacy Policy, which
          explains how we collect, use, and protect your personal information.
        </Typography>
      </>
    ),
  },
  {
    id: "beta-status",
    title: "3. Beta Status & Free Service",
    content: (
      <>
        <Typography variant="body1" paragraph>
          The Service is currently free during the beta stage.
        </Typography>
        <Typography variant="body1" paragraph>
          Contactie
          <Box
            component="sup"
            sx={{ fontSize: "0.6em", verticalAlign: "super" }}
          >
            TM
          </Box>{" "}
          may introduce paid subscriptions, goods, or services in the future.
        </Typography>
        <Typography variant="body1" paragraph>
          Features may change, be removed, or be unstable during the beta.
        </Typography>
        <Typography variant="body1" paragraph>
          By using the Service, You acknowledge and accept that it is provided
          “as-is”, and We are not liable for bugs, errors, or interruptions.
        </Typography>
      </>
    ),
  },
  {
    id: "user-accounts",
    title: "4. User Accounts & Information",
    content: (
      <>
        <Typography variant="body1" paragraph>
          When creating an Account, You must provide accurate and current
          information.
        </Typography>
        <Typography variant="body1" paragraph>
          Required information includes: first and last name, email, and phone
          number.
        </Typography>
        <Typography variant="body1" paragraph>
          Optional information may include: social media links, profile
          pictures, and other public content.
        </Typography>
        <Typography variant="body1" paragraph>
          You are responsible for safeguarding your password and all activity
          under your Account.
        </Typography>
        <Typography variant="body1" paragraph>
          Notify Us immediately if you suspect unauthorized access.
        </Typography>
      </>
    ),
  },
  {
    id: "public-private-data",
    title: "5. Public & Private Data",
    content: (
      <>
        <Typography variant="body1" paragraph>
          Required data (email, phone) is private and will be protected in
          accordance with our Privacy Policy.
        </Typography>
        <Typography variant="body1" paragraph>
          Optional data (social media links, profile pictures, etc.) may be
          displayed publicly by You and other users.
        </Typography>
        <Typography variant="body1" paragraph>
          By posting Content, You consent to it being displayed publicly.
        </Typography>
        <Typography variant="body1" paragraph>
          We are not responsible for how public data is viewed or used by other
          users.
        </Typography>
      </>
    ),
  },
  {
    id: "content-posting",
    title: "6. Content Posting & Responsibility",
    content: (
      <>
        <Typography variant="body1" paragraph>
          You are solely responsible for all Content You post.
        </Typography>
        <Typography variant="body1" paragraph>
          You may not post Content that is unlawful, defamatory, offensive,
          spam, or infringing on others’ rights.
        </Typography>
        <Typography variant="body1" paragraph>
          We may remove Content that violates these Terms, but are not
          responsible for all user Content.
        </Typography>
        <Typography variant="body1" paragraph>
          Maintain your own copy of Content, as We do not guarantee backups.
        </Typography>
      </>
    ),
  },
  {
    id: "intellectual-property",
    title: "7. Intellectual Property",
    content: (
      <>
        <Typography variant="body1" paragraph>
          Contactie
          <Box
            component="sup"
            sx={{ fontSize: "0.6em", verticalAlign: "super" }}
          >
            TM
          </Box>{" "}
          owns all original content, features, and functionality.
        </Typography>
        <Typography variant="body1" paragraph>
          Users retain ownership of their Content but grant Contactie
          <Box
            component="sup"
            sx={{ fontSize: "0.6em", verticalAlign: "super" }}
          >
            TM
          </Box>{" "}
          a license to use it within the Service.
        </Typography>
        <Typography variant="body1" paragraph>
          We respect the intellectual property rights of others. If you believe
          Content on the Service infringes on a copyright, please submit your
          claim via email to{" "}
          <Link href="mailto:contact@contactie.com">contact@contactie.com</Link>{" "}
          with the subject line: "Copyright Infringement". We will take whatever
          action we deem appropriate, including removal of the challenged
          content.*
        </Typography>
      </>
    ),
  },
  {
    id: "third-party-links",
    title: "8. Links to Other Websites",
    content: (
      <>
        <Typography variant="body1" paragraph>
          Our Service may include links to third-party sites.
        </Typography>
        <Typography variant="body1" paragraph>
          We are not responsible for third-party content, privacy practices, or
          services.
        </Typography>
      </>
    ),
  },
  {
    id: "termination",
    title: "9. Termination",
    content: (
      <>
        <Typography variant="body1" paragraph>
          We may terminate or suspend Your Account immediately, without prior
          notice or liability, for any reason whatsoever, including without
          limitation if You breach these Terms.
        </Typography>
        <Typography variant="body1" paragraph>
          Upon termination, your access to the Service ceases immediately.
        </Typography>
      </>
    ),
  },
  {
    id: "liability-limitation",
    title: "10. Limitation of Liability",
    content: (
      <>
        <Typography
          variant="body1"
          paragraph
          component="div"
          sx={{ textTransform: "uppercase", fontWeight: "bold" }}
        >
          THE SERVICE IS PROVIDED TO YOU "AS IS" AND "AS AVAILABLE" WITHOUT
          WARRANTY OF ANY KIND. Contactie
          <Box
            component="sup"
            sx={{ fontSize: "0.6em", verticalAlign: "super" }}
          >
            TM
          </Box>
          , ON ITS OWN BEHALF AND ON BEHALF OF ITS OWNERS, EXPRESSLY DISCLAIMS
          ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR OTHERWISE.
        </Typography>
        <Typography
          variant="body1"
          paragraph
          component="div"
          sx={{ textTransform: "uppercase", fontWeight: "bold", mt: 2 }}
        >
          IN NO EVENT SHALL Contactie
          <Box
            component="sup"
            sx={{ fontSize: "0.6em", verticalAlign: "super" }}
          >
            TM
          </Box>{" "}
          OR ITS OWNERS BE LIABLE FOR ANY SPECIAL, INDIRECT, OR CONSEQUENTIAL
          DAMAGES WHATSOEVER. TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE TOTAL
          LIABILITY OF THE COMPANY AND ITS OWNERS SHALL NOT EXCEED ZERO TUNISIAN
          DINARS (0TND).
        </Typography>
      </>
    ),
  },
  {
    id: "indemnification",
    title: "11. Indemnification",
    content: (
      <Typography variant="body1" paragraph>
        You agree to defend, indemnify, and hold harmless the Company and its
        owners, agents, and affiliates from and against any and all claims,
        damages, liabilities, and expenses (including attorney's fees) arising
        from: (a) Your use of the Service; (b) Your violation of these Terms; or
        (c) Content You post on the Service.
      </Typography>
    ),
  },
  {
    id: "governing-law",
    title: "12. Governing Law & Disputes",
    content: (
      <>
        <Typography variant="body1" paragraph>
          These Terms are governed by Tunisian law.
        </Typography>
        <Typography variant="body1" paragraph>
          You agree that any legal action or proceeding arising out of these
          Terms shall be brought exclusively in the courts located in Tunisia.
        </Typography>
      </>
    ),
  },
  {
    id: "changes-to-terms",
    title: "13. Changes to Terms",
    content: (
      <>
        <Typography variant="body1" paragraph>
          We may update these Terms at any time.
        </Typography>
        <Typography variant="body1" paragraph>
          Continued use of the Service after updates constitutes agreement to
          the new Terms.
        </Typography>
      </>
    ),
  },
  {
    id: "contact",
    title: "14. Contact",
    content: (
      <Typography variant="body1" paragraph>
        If you have questions, contact us at: contact@contactie.com
      </Typography>
    ),
  },
];

export default function TermsOfUsePage() {
  const theme = useTheme();

  return (
    <>
      <Helmet>
        <title>Terms of Service (Beta) | Contactie™</title>
        <meta
          name="description"
          content="Official Beta Terms of Service for the Contactie™ platform. By using our service, you agree to these terms."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <StyledPageContainer>
        <Box
          component="header"
          sx={{ py: 3, px: 5, display: { xs: "none", md: "flex" } }}
        >
          <Logo sx={{ height: 40, width: "auto" }} />
        </Box>

        <ContentWrapper>
          <Container maxWidth="lg">
            <Box sx={{ mb: 5, textAlign: { xs: "center", md: "left" } }}>
              <Box
                sx={{
                  mb: 5,
                  display: { xs: "flex", md: "none" },
                  justifyContent: "center",
                }}
              >
                <Logo sx={{ height: 40, width: "auto" }} />
              </Box>
              <Typography variant="h2" component="h1" sx={{ fontWeight: 700 }}>
                Contactie
                <Box
                  component="sup"
                  sx={{ fontSize: "0.6em", verticalAlign: "super" }}
                >
                  TM
                </Box>{" "}
                Terms of Service (Beta)
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Last Updated:{" "}
                {new Date().toLocaleString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Typography>
            </Box>

            <Grid container spacing={5}>
              <Grid item xs={12} md={8}>
                {sections.map((section) => (
                  <Box key={section.id} id={section.id} sx={{ mb: 5 }}>
                    <Typography
                      variant="h4"
                      component="h2"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {section.title}
                    </Typography>
                    {section.content}
                  </Box>
                ))}
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    position: "sticky",
                    top: theme.spacing(4),
                    display: { xs: "none", md: "block" },
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    On this page
                  </Typography>
                  <List dense>
                    {sections.map((section) => (
                      <ListItem key={section.id} disablePadding>
                        <Link
                          href={`#${section.id}`}
                          underline="hover"
                          sx={{ color: "text.secondary", py: 0.5 }}
                        >
                          {section.title}
                        </Link>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </ContentWrapper>
        <SimpleFooter />
      </StyledPageContainer>
    </>
  );
}
